import { useState, useEffect, useCallback, useRef } from 'react';

const axios = require('axios').default;

// `contact` is the donation subject (a Fan or an Organization); both expose an
// `id`. Pass `donationsUrl` to point at a different endpoint (e.g. the org
// donations tab); it defaults to the fan donations endpoint.
export default function useDonations({ team, contact, csrfToken, donationsUrl, enabled = true }) {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [count, setCount] = useState(null);
  const [totalDonated, setTotalDonated] = useState('');
  const [softCreditTotal, setSoftCreditTotal] = useState('');
  const [totalContribution, setTotalContribution] = useState('');
  const [lastDate, setLastDate] = useState('');
  const [firstDate, setFirstDate] = useState('');
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statementYears, setStatementYears] = useState([]);
  const [receiptsEnabled, setReceiptsEnabled] = useState(false);
  const [missingTeamFields, setMissingTeamFields] = useState([]);
  const [giftFields, setGiftFields] = useState([]);
  const [statementYear, setStatementYear] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  // Tracks the active GET request so paging/reload cancels stale responses
  // and don't clobber newer data via setState-after-resolve. Axios 0.19 predates
  // AbortController support, so we use the CancelToken API.
  const activeFetchRef = useRef(null);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return;
    if (!team || !contact || !contact.id) return;

    const fetchDonations = async () => {
      if (activeFetchRef.current) activeFetchRef.current.cancel('stale');
      const source = axios.CancelToken.source();
      activeFetchRef.current = source;

      try {
        setLoading(true);
        setError('');
        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }

        const response = await axios.get(
          donationsUrl || `/teams/${team.id}/crm/fans/${contact.id}/donations.json`,
          {
            params: {
              page,
              per_page: perPage,
            },
            cancelToken: source.token,
          },
        );
        const data = response.data || {};
        const donationsFromResponse = data.donations || [];
        const pagination = data.pagination || {};

        setDonations(donationsFromResponse);
        if (typeof data.donation_count === 'number') {
          setCount(data.donation_count);
        } else {
          setCount(donationsFromResponse.length);
        }
        setTotalDonated(data.total_donated || '');
        setSoftCreditTotal(data.soft_credit_total || '');
        setTotalContribution(data.total_contribution || '');
        setLastDate(data.last_donation_date || '');
        setFirstDate(data.first_donation_date || '');
        setTotalEntries(pagination.total_entries || donationsFromResponse.length || 0);
        setTotalPages(pagination.total_pages || 1);
        setReceiptsEnabled(!!data.receipts_enabled);
        setMissingTeamFields(data.missing_team_fields || []);
        setGiftFields(data.gift_fields || []);
        const years = data.statement_years || [];
        setStatementYears(years);
        setStatementYear((prev) => prev || (years.length > 0 ? String(years[0]) : ''));
      } catch (err) {
        if (axios.isCancel(err)) return; // page/reload changed mid-flight
        setError('Failed to load donations');
      } finally {
        if (activeFetchRef.current === source) {
          activeFetchRef.current = null;
          setLoading(false);
        }
      }
    };

    fetchDonations();
  }, [team?.id, contact?.id, csrfToken, donationsUrl, page, perPage, reloadToken, enabled]);

  return {
    donations,
    loading,
    error,
    count,
    totalDonated,
    softCreditTotal,
    totalContribution,
    lastDate,
    firstDate,
    page,
    setPage,
    perPage,
    totalEntries,
    totalPages,
    reload,
    statementYears,
    receiptsEnabled,
    missingTeamFields,
    giftFields,
    statementYear,
    setStatementYear,
  };
}
